import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, PillGroup } from '@base-app';
import { DateRangeOptionId, DateRangeSelection } from '@builder';
import { getFirstDayOfMonth, getLastDayOfMonth } from '@/common/utils';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import useConsentedParticipantsStore from '@/modules/my-account/store/useConsentedParticipantsStore/useConsentedParticipantsStore';
import { Pill } from '@/common/components/base-app/organisms/PillGroup/PillGroup.types';
import FeatureFlags from '@/modules/feature-flags/types/feature-flags.types';
import { Permissions } from '@modules/permissions/types';
import { usePermissions } from '@modules/permissions/hooks';
import { useFeatureFlags } from '@/modules/feature-flags/hooks';
import { TFunction } from 'i18next';
import { LayoutThreeContent } from './LayoutThreeContent';
import { PillId } from './LayoutThree.types';
import useQueryHhcParticipantData from '@/modules/study-insights/hooks/useQueryHhcParticipantData';
import { useLayoutThreeStore } from '@/modules/study-insights/store';
import { GroupKey } from '@/modules/study-insights/types/api';
import { useQueryEditGoals } from '@/modules/study-insights/hooks';

const getPillList = (t: TFunction): Pill[] => {
  return [
    {
      label: t('consented_participants.title'),
      id: PillId.CONSENTED_PARTICIPANTS,
    },
    {
      label: t('campaign_tracking.title'),
      id: PillId.CAMPAIGN_TRACKING,
    },
  ];
};

const getGroupKeyFromDateRangeOption = (dateRangeOption: DateRangeOptionId | undefined): GroupKey => {
  switch (dateRangeOption) {
    case DateRangeOptionId.THIS_WEEK:
    case DateRangeOptionId.CUSTOM:
      return GroupKey.BY_DAY;
    case DateRangeOptionId.THIS_YEAR:
    case DateRangeOptionId.ALL_TIME:
      return GroupKey.BY_MONTH;
    default:
      return GroupKey.BY_WEEK;
  }
};

const LayoutThree: FC = () => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [endDate, setEndDate] = useState<Date | undefined>(getLastDayOfMonth());
  const [startDate, setStartDate] = useState<Date | undefined>(getFirstDayOfMonth());
  const [groupKey, setGroupKey] = useState<GroupKey>(GroupKey.BY_WEEK);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeSelection>();
  const [selectedPill, setSelectedPill] = useState<PillId>(PillId.CONSENTED_PARTICIPANTS);

  const { isFeatureEnabled } = useFeatureFlags();
  const { hasPermission } = usePermissions();
  const hasCampaignTrackingPartnerLevel = hasPermission(Permissions.CAMPAIGN_TRACKING);
  const isCampaingTrackingEnabled = isFeatureEnabled(FeatureFlags.CAMPAIGN_TRACKING) && hasCampaignTrackingPartnerLevel;

  const handleOnClickPill = (id: string): void => {
    setSelectedPill(id as PillId);
  };

  const { setIsLoading, setIsError, setIsSuccess, setMapData, setData } = useLayoutThreeStore();

  const {
    data: consentedParticipantData,
    isLoading,
    isError,
  } = useQueryHhcParticipantData({
    startDate,
    endDate,
    groupKey,
  });

  useEffect(() => {
    if (consentedParticipantData?.groupData.locationSeries == null) {
      return;
    }

    const mappedData = consentedParticipantData?.groupData.locationSeries.map((item) => ({
      id: item.groupValue,
      value: item.consentValue,
      name: item.groupValue,
    }));
    setMapData(mappedData);
    setData(consentedParticipantData ?? undefined);
    if (consentedParticipantData) {
      setIsSuccess(true);
    }
  }, [consentedParticipantData]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setIsError(isError);
  }, [isError]);

  const { setEndDate: setStoreEndDate, setStartDate: setStoreStartDate } = useConsentedParticipantsStore();

  const { data: editGoalsData } = useQueryEditGoals();

  useEffect(() => {
    if (!editGoalsData) {
      return;
    }
    setStoreStartDate(editGoalsData.startDate);
    setStoreEndDate(editGoalsData.endDate);
  }, [editGoalsData]);

  const handleSelectDateRange = (selection: DateRangeSelection): void => {
    if (selection.selectedDateRange === DateRangeOptionId.ALL_TIME) {
      setStartDate(undefined);
      setEndDate(undefined);
    } else {
      setStartDate(selection.startDate);
      setEndDate(selection.endDate);
    }
    setGroupKey(getGroupKeyFromDateRangeOption(selection.selectedDateRange));
    setSelectedDateRange(selection);
  };

  return (
    <>
      <Box
        css={{
          margin: 'auto',
          width: '100%',
          maxWidth: '$studyInsights$containerMaxWidth',
          paddingLeft: '$8',
          paddingBottom: '$6',
        }}
      >
        {isCampaingTrackingEnabled && (
          <PillGroup items={getPillList(t)} selectedPill={selectedPill} onClickPill={handleOnClickPill} />
        )}
      </Box>
      <LayoutThreeContent
        selectedPillId={selectedPill}
        t={t}
        selectedDateRange={selectedDateRange}
        onSelectDateRange={handleSelectDateRange}
      />
    </>
  );
};

export default LayoutThree;
